// Realtime da Agenda de Rotas (spec Bruno §3.2): reflete visitas lançadas de qualquer
// lugar do CRM (Kanban, cluster, prospecção) sem refresh manual — o contador de "Criar
// Rota Inteligente (N)" e as rotas ativas se atualizam sozinhos.
//
// Filtros de postgres_changes só suportam igualdade simples; como a tela filtra por N
// responsáveis × várias janelas de data, escutamos sem filtro server-side e deixamos os
// callbacks (com debounce) refazerem os fetches correntes — idempotente e barato.

import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface UseAgendaRealtimeArgs {
    enabled: boolean;
    onTarefasChange: () => void;
    onRotasChange?: () => void;
    debounceMs?: number;
}

export function useAgendaRealtime({ enabled, onTarefasChange, onRotasChange, debounceMs = 1500 }: UseAgendaRealtimeArgs): void {
    // Refs mantêm os callbacks atuais sem recriar o channel a cada render.
    const tarefasCb = useRef(onTarefasChange);
    const rotasCb = useRef(onRotasChange);
    useEffect(() => { tarefasCb.current = onTarefasChange; }, [onTarefasChange]);
    useEffect(() => { rotasCb.current = onRotasChange; }, [onRotasChange]);

    useEffect(() => {
        if (!enabled) return;

        let tTarefas: ReturnType<typeof setTimeout> | null = null;
        let tRotas: ReturnType<typeof setTimeout> | null = null;
        const bounceTarefas = () => { if (tTarefas) clearTimeout(tTarefas); tTarefas = setTimeout(() => tarefasCb.current?.(), debounceMs); };
        const bounceRotas = () => { if (tRotas) clearTimeout(tRotas); tRotas = setTimeout(() => rotasCb.current?.(), debounceMs); };

        const channel = supabase
            .channel(`agenda-realtime-${Math.random().toString(36).slice(2, 8)}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'tarefas' }, bounceTarefas)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'rotas_comerciais' }, bounceRotas)
            .subscribe(status => {
                // Se a publication não estiver disponível, degrada em silêncio: a tela
                // continua atualizando via refresh pós-ação (comportamento anterior).
                if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                    console.warn('Agenda realtime indisponível:', status);
                }
            });

        return () => {
            if (tTarefas) clearTimeout(tTarefas);
            if (tRotas) clearTimeout(tRotas);
            supabase.removeChannel(channel);
        };
    }, [enabled, debounceMs]);
}
